import { MapUtils } from '../../../utility/map-utils.js';
import { isSameData } from '../../dto/data-model-utils.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../../dto/data-model.js';
import { TimeZone } from '../../../time/timezone.js';
import { Dayjs } from 'dayjs';
import { fromEnumAsString, fromEnumAsStringOrNull, toEnumFromString, toEnumFromStringOrNull } from '../../../serialization/enum-utils.js';
import { CustomerOrderBase } from '../../../contracts/customer-order.js';
import { VersionRange } from '../../core/version-range.js';
import { TrueTime } from '../../../time/true-time.js';
import { OnlineEventTicketOrder } from '../online-event-ticket-order.js';
import { EventTicketOrder } from '../event-ticket-order.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { InvoiceScope } from './invoice-group.js';

export enum InvoiceRetryRequestStatus {
  Pending = 'PENDING',
  Completed = 'COMPLETED',
}

export enum InvoiceRetryStrategy {
  RetryAll = 'RetryAll',
  RetryScope = 'RetryScope',
}

export class InvoiceRetryRequest extends DataModel {
  static readonly TYPE_ID: string = 'a8f3c4d1e2b54f6c9a7d8e5b3c2f1a4e';
  static readonly SCHEMA_VERSION: number = 3;

  constructor(
    public readonly uniqueId: string,
    public readonly creationDate: Dayjs,
    public readonly status: InvoiceRetryRequestStatus,
    public readonly strategy: InvoiceRetryStrategy,
    public readonly order: CustomerOrderBase,
    public readonly paymentAccountId: string,
    public readonly errorCode: string,
    public readonly scope: InvoiceScope | null,
  ) {
    super();
  }

  get $typeId(): string {
    return InvoiceRetryRequest.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, InvoiceRetryRequest.SCHEMA_VERSION);
  }

  get businessRef(): BusinessModuleRef {
    return this.order.businessRef;
  }

  static forAll({ order, paymentAccountId, errorCode }: {
      order: CustomerOrderBase,
      paymentAccountId: string,
      errorCode: string,
  }): InvoiceRetryRequest {
    return new InvoiceRetryRequest(
        order.uniqueId,
        TrueTime.now(),
        InvoiceRetryRequestStatus.Pending,
        InvoiceRetryStrategy.RetryAll,
        order,
        paymentAccountId,
        errorCode,
        null
    );
  }

  static forScope({ order, paymentAccountId, errorCode, scope }: {
      order: CustomerOrderBase,
      paymentAccountId: string,
      errorCode: string,
      scope: InvoiceScope,
  }): InvoiceRetryRequest {
    return new InvoiceRetryRequest(
        order.uniqueId,
        TrueTime.now(),
        InvoiceRetryRequestStatus.Pending,
        InvoiceRetryStrategy.RetryScope,
        order,
        paymentAccountId,
        errorCode,
        scope,
    );
  }

  static fromMap(
      context: SerializationContext,
      map: SerializedData,
  ): InvoiceRetryRequest {
    const order = context.fromData<CustomerOrderBase>(
        MapUtils.getRecord(map, 'order'),
    );

    return new InvoiceRetryRequest(
        MapUtils.getString(map, 'uniqueId'),
        context.decodeDate(MapUtils.getInteger(map, 'creationDate')),
        toEnumFromString(MapUtils.getString(map, '$status'), InvoiceRetryRequestStatus),
        toEnumFromString(MapUtils.getString(map, 'strategy'), InvoiceRetryStrategy),
        order,
        MapUtils.getString(map, 'paymentAccountId'),
        MapUtils.getString(map, 'errorCode'),
        toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'scope'), InvoiceScope),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creationDate': context.encodeDate(this.creationDate),
      '$status': fromEnumAsString(this.status),
      'strategy': fromEnumAsString(this.strategy),
      'order': context.toData(this.order),
      'paymentAccountId': this.paymentAccountId,
      'errorCode': this.errorCode,
      'businessRef': this.order.businessRef.encode(),
      'orderId': this.order.uniqueId,
      'eventId': this.order instanceof EventTicketOrder ?
        this.order.eventId :
        (this.order instanceof OnlineEventTicketOrder ?
        this.order.eventId :
        null),
      'scope': fromEnumAsStringOrNull(this.scope),
    };
  }

  clone(): this {
    return new InvoiceRetryRequest(
        this.uniqueId,
        TimeZone.cloneDate(this.creationDate),
        this.status,
        this.strategy,
        this.order.clone(),
        this.paymentAccountId,
        this.errorCode,
        this.scope,
    ) as this;
  }

  isSame(other: InvoiceRetryRequest): boolean {
    if(this === other) {
      return true;
    }
    return other.uniqueId === this.uniqueId &&
      other.creationDate === this.creationDate &&
      other.status === this.status &&
      other.strategy === this.strategy &&
      isSameData(other.order, this.order) &&
      other.paymentAccountId === this.paymentAccountId &&
      other.errorCode === this.errorCode &&
      other.scope === this.scope;
  }

  copyWith(data: Partial<InvoiceRetryRequest>): this {
    return new InvoiceRetryRequest(
        data.uniqueId ?? this.uniqueId,
        data.creationDate ?? TimeZone.cloneDate(this.creationDate),
        data.status ?? this.status,
        data.strategy ?? this.strategy,
        data.order ?? this.order.clone(),
        data.paymentAccountId ?? this.paymentAccountId,
        data.errorCode ?? this.errorCode,
        data.scope ?? this.scope,
    ) as this;
  }

  static encodedStatusSafe(status: InvoiceRetryRequestStatus): string {
    return fromEnumAsString(status);
  }
}

export class InvoiceRetryRequestBuilder extends DataModelBuilder<InvoiceRetryRequest> {
  build(context: SerializationContext, map: SerializedData): InvoiceRetryRequest {
    return InvoiceRetryRequest.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(InvoiceRetryRequest.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(InvoiceRetryRequest.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In version 2 we added `strategy` of type `InvoiceRetryStrategy`
    if(version < 2) {
      map['strategy'] = fromEnumAsString(InvoiceRetryStrategy.RetryAll);
      version = 2;
    }

    // In version 3 we added `scope` of type `InvoiceScope | null`
    if(version < 3) {
      version = 3;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
